import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Like `useReducer`, but hydrates initial state from `localStorage[key]`
 * (when present and parsable) and persists state on every dispatch.
 *
 * Returns `[state, dispatch, clear]`. `clear()` removes the localStorage
 * entry and resets state to a fresh `init(initArg)`.
 */
export function useLocalStorageReducer<S, A, I>(
  key: string,
  reducer: (state: S, action: A) => S,
  initArg: I,
  init: (arg: I) => S,
  replacer?: (key: string, value: unknown) => unknown,
): [S, (action: A) => void, () => void] {
  const [state, setState] = useState<S>(() => readStored<S>(key) ?? init(initArg));

  // Skip the first effect: state on mount equals what we just read from (or
  // initialised in the absence of) localStorage. Re-writing it would be wasted.
  const isFirstWrite = useRef(true);
  useEffect(() => {
    if (isFirstWrite.current) {
      isFirstWrite.current = false;
      return;
    }
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(key, JSON.stringify(state, replacer));
    } catch {
      // Quota / serialisation failures must not break the form.
    }
  }, [key, state, replacer]);

  const dispatch = useCallback(
    (action: A) => {
      setState((current) => reducer(current, action));
    },
    [reducer],
  );

  const clear = useCallback(() => {
    if (typeof window !== 'undefined') {
      window.localStorage.removeItem(key);
    }
    isFirstWrite.current = true;
    setState(init(initArg));
  }, [key, init, initArg]);

  return [state, dispatch, clear];
}

function readStored<S>(key: string): S | null {
  if (typeof window === 'undefined') return null;
  const raw = window.localStorage.getItem(key);
  if (raw === null) return null;
  try {
    return JSON.parse(raw) as S;
  } catch {
    return null;
  }
}
